import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import YapperModal from '../components/YapperModal';
import { Card } from '../components/ds/core/Card';
import { Badge } from '../components/ds/core/Badge';
import { Button } from '../components/ds/core/Button';
import AdSlot from '../components/AdSlot';
import './AnnouncementsView.css';

function AnnouncementsView() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [updating, setUpdating] = useState(false);
  const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null, showCancel: false });

  const showAlert = (title, message, type = 'info') => {
    setModal({ isOpen: true, title, message, type, onConfirm: () => setModal(m => ({ ...m, isOpen: false })), showCancel: false });
  };

  const showConfirm = (title, message, onConfirmCallback, type = 'warning') => {
    setModal({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setModal(m => ({ ...m, isOpen: false }));
        if (onConfirmCallback) onConfirmCallback();
      },
      showCancel: true
    });
  };

  useEffect(() => {
    loadAnnouncements();
    checkAdminStatus();
  }, []);

  const checkAdminStatus = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (userId) {
        const adminDoc = await getDoc(doc(db, 'admins', userId));
        setIsAdmin(adminDoc.exists());
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const announcementsQuery = query(
        collection(db, 'announcements'),
        orderBy('timestamp', 'desc')
      );
      const snapshot = await getDocs(announcementsQuery);

      const announcementsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setAnnouncements(announcementsList);
    } catch (error) {
      console.error('Error loading announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (announcement) => {
    setEditingId(announcement.id);
    setEditText(announcement.text);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
  };

  const handleSaveEdit = async (announcementId) => {
    if (!editText.trim()) {
      showAlert('Empty Content', 'Announcement cannot be empty', 'warning');
      return;
    }

    setUpdating(true);
    try {
      await updateDoc(doc(db, 'announcements', announcementId), {
        text: editText.trim()
      });

      // Update local state
      setAnnouncements(announcements.map(a =>
        a.id === announcementId ? { ...a, text: editText.trim() } : a
      ));

      setEditingId(null);
      setEditText('');
      showAlert('Success', 'Announcement updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating announcement:', error);
      showAlert('Error', 'Error updating announcement: ' + error.message, 'error');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (announcementId) => {
    showConfirm('Delete Announcement', 'Are you sure you want to delete this announcement? This action cannot be undone.', async () => {
      try {
        await deleteDoc(doc(db, 'announcements', announcementId));

        // Update local state
        setAnnouncements(announcements.filter(a => a.id !== announcementId));

        showAlert('Deleted', 'Announcement deleted successfully!', 'success');
      } catch (error) {
        console.error('Error deleting announcement:', error);
        showAlert('Error', 'Error deleting announcement: ' + error.message, 'error');
      }
    });
  };

  if (loading) {
    return (
      <div className="announcements-view">
        <div className="announcements-container">
          <div className="announcements-header">
            <h1>📢 Announcements</h1>
          </div>
          <div className="loading-announcements">Loading announcements...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="announcements-view">
      <div className="announcements-container">
        <div className="announcements-header">
          <h1>📢 Announcements</h1>
          <p className="announcements-subtitle">Official updates from the Yapp team</p>
        </div>

        {announcements.length === 0 ? (
          <div className="no-announcements">
            <p>No announcements yet. Check back later!</p>
          </div>
        ) : (
          <div className="announcements-list">
            {announcements.map(announcement => (
              <Card
                key={announcement.id}
                variant="glass"
                padding="none"
                hover
                className="announcement-card"
                style={{ background: '#e6e7eb', color: 'var(--text-body)', border: '1px solid var(--border-subtle)', boxShadow: 'var(--shadow-md)', backdropFilter: 'none' }}
              >
                <div className="announcement-header">
                  <Badge tone="premium" icon={<span aria-hidden="true">⭐</span>}>
                    Admin {announcement.adminUsername || ''}
                  </Badge>
                  <div className="announcement-header-right">
                    <span className="announcement-date">
                      {announcement.timestamp?.toDate?.()?.toLocaleDateString()} at{' '}
                      {announcement.timestamp?.toDate?.()?.toLocaleTimeString()}
                    </span>
                    {isAdmin && (
                      <div className="admin-actions">
                        <Button
                          variant="guest"
                          size="sm"
                          className="edit-btn"
                          onClick={() => handleStartEdit(announcement)}
                          title="Edit announcement"
                        >
                          ✏️
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          className="delete-btn"
                          onClick={() => handleDelete(announcement.id)}
                          title="Delete announcement"
                        >
                          🗑️
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="announcement-content">
                  <p>{announcement.text}</p>
                </div>
              </Card>
            ))}
          </div>
        )}

        <AdSlot format="horizontal" style={{ maxWidth: 970 }} />

        {/* Edit Modal */}
        {editingId && (
          <div className="edit-modal-overlay" onClick={handleCancelEdit}>
            <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
              <button className="close-modal-btn" onClick={handleCancelEdit}>×</button>
              <h2>✏️ Edit Announcement</h2>
              <textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                disabled={updating}
                maxLength={1000}
                rows={5}
              />
              <div className="modal-actions">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancelEdit}
                  className="cancel-btn"
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => handleSaveEdit(editingId)}
                  disabled={updating || !editText.trim()}
                  className="save-btn"
                >
                  {updating ? '⏳ Saving...' : '💾 Save'}
                </Button>
              </div>
            </div>
          </div>
        )}

        <YapperModal
          isOpen={modal.isOpen}
          title={modal.title}
          message={modal.message}
          type={modal.type}
          onConfirm={modal.onConfirm}
          onCancel={modal.showCancel ? () => setModal(m => ({ ...m, isOpen: false })) : null}
        />
      </div>
    </div>
  );
}

export default AnnouncementsView;
